import React, { useState } from 'react';
import { CryptoAddCalculator } from '../utils/cryptoAddCalculator';

const Tools: React.FC = () => {
  const [showCalculator, setShowCalculator] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-6 text-center">🛠️ 工具页面</h1>

          <div className="space-y-4">
            <button
              onClick={() => setShowCalculator(true)}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center space-x-2"
            >
              <span className="text-xl">💰</span>
              <span>成本试算工具</span>
            </button>
          </div>
        </div>
      </div>

      {showCalculator && <CostCalculatorModal onClose={() => setShowCalculator(false)} />}
    </div>
  );
};

interface CostCalculatorModalProps {
  onClose: () => void;
}

const CostCalculatorModal: React.FC<CostCalculatorModalProps> = ({ onClose }) => {
  const [formData, setFormData] = useState({
    initialCost: '',
    currentValue: '',
    currentPrice: '',
    addAmount: '',
    addPrice: '',
    targetCost: '',
  });

  const [result, setResult] = useState<string>('');
  const [error, setError] = useState<string>('');

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError('');
    setResult('');
  };

  const calculate = () => {
    try {
      const { initialCost, currentValue, currentPrice, addAmount, addPrice, targetCost } = formData;

      // 检查必填字段
      if (!initialCost || !currentValue || !currentPrice) {
        throw new Error('初始成本价、当前持仓总量、当前价格为必填项');
      }

      const P0 = parseFloat(initialCost);
      const V = parseFloat(currentValue);
      const Pc = parseFloat(currentPrice);

      // 验证必填字段
      if (P0 <= 0 || V <= 0 || Pc <= 0) {
        throw new Error('初始成本价、当前持仓总量、当前价格必须大于0');
      }

      let resultText = '';

      // 情况1：没有输入目标成本价，计算新成本价
      if (!targetCost) {
        if (!addAmount || !addPrice) {
          throw new Error('计算新成本价需要输入加仓金额和加仓价格');
        }

        const X = parseFloat(addAmount);
        const Pa = parseFloat(addPrice);

        if (X <= 0 || Pa <= 0) {
          throw new Error('加仓金额和加仓价格必须大于0');
        }

        const newCost = CryptoAddCalculator.calculateNewCost(P0, V, Pc, X, Pa);

        // 计算加仓后新成本价相对于加仓价格的盈亏百分比
        const addPriceProfitRate = CryptoAddCalculator.calculateProfitRate(newCost, Pa);

        resultText = `加仓后新成本价: ${newCost.toFixed(6)}\n加仓后新成本价盈亏: ${addPriceProfitRate.toFixed(2)}%`;
      }
      // 情况2：输入了目标成本价和加仓金额，计算加仓价格
      else if (targetCost && addAmount && !addPrice) {
        const Pt = parseFloat(targetCost);
        const X = parseFloat(addAmount);

        if (Pt <= 0 || X <= 0) {
          throw new Error('目标成本价和加仓金额必须大于0');
        }

        // 验证目标成本价是否合理
        // 如果当前亏损，目标成本价必须小于当前成本价且大于当前价格
        // 如果当前盈利，目标成本价必须大于当前成本价且小于当前价格（这通常不是补仓逻辑，但数学上成立）
        // 简单验证：新成本价介于当前价格和原成本价之间
        if ((Pt - P0) * (Pt - Pc) >= 0) {
           throw new Error('目标成本价必须介于当前成本价和当前价格之间');
        }

        const calculatedAddPrice = CryptoAddCalculator.calculateAddPrice(P0, V, Pc, Pt, X);
        
        // 计算加仓价格相对于当前价格的偏差
        const deviation = ((calculatedAddPrice - Pc) / Pc) * 100;

        resultText = `建议挂单加仓价格: ${calculatedAddPrice.toFixed(6)}\n(相对于当前价格 ${deviation > 0 ? '+' : ''}${deviation.toFixed(2)}%)`;
      }
      // 情况3：输入了目标成本价和加仓价格，计算加仓金额
      else if (targetCost && !addAmount && addPrice) {
         const Pt = parseFloat(targetCost);
         const Pa = parseFloat(addPrice);

         if (Pt <= 0 || Pa <= 0) {
            throw new Error('目标成本价和加仓价格必须大于0');
         }

          // 验证目标成本价是否合理
          if ((Pt - P0) * (Pt - Pa) >= 0) {
             throw new Error('目标成本价必须介于当前成本价和加仓价格之间');
          }

         const calculatedAddAmount = CryptoAddCalculator.calculateAddAmount(P0, V, Pc, Pt, Pa);
         
         resultText = `需要加仓金额: ${calculatedAddAmount.toFixed(2)} USDT`;
      }
      else {
         throw new Error('请选择一种计算模式：\n1. 输入加仓金额+加仓价格 -> 算新成本\n2. 输入目标成本+加仓金额 -> 算挂单价\n3. 输入目标成本+加仓价格 -> 算加仓金额');
      }

      setResult(resultText);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const clearForm = () => {
    setFormData({
      initialCost: '',
      currentValue: '',
      currentPrice: '',
      addAmount: '',
      addPrice: '',
      targetCost: '',
    });
    setResult('');
    setError('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="relative bg-gradient-to-br from-purple-900/95 via-blue-900/95 to-indigo-900/95 border border-white/20 rounded-xl shadow-2xl max-w-md w-full min-h-[50vh] max-h-[90vh] overflow-y-auto">
        {/* 背景光晕效果 */}
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-tr from-cyan-500/10 via-transparent to-purple-500/10 rounded-xl pointer-events-none" />
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-cyan-400/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-purple-400/10 rounded-full blur-3xl pointer-events-none" />

        {/* 内容 */}
        <div className="relative z-10 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white">💰 成本试算工具</h2>
            <button onClick={onClose} className="text-white/70 hover:text-white transition-colors text-2xl">
              ×
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white/90 mb-1">初始成本价 *</label>
              <input
                type="number"
                step="0.000001"
                value={formData.initialCost}
                onChange={(e) => handleInputChange('initialCost', e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
                placeholder="例如: 0.28318"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/90 mb-1">当前持仓总量 *</label>
              <input
                type="number"
                step="0.01"
                value={formData.currentValue}
                onChange={(e) => handleInputChange('currentValue', e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
                placeholder="例如: 32000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/90 mb-1">当前价格 *</label>
              <input
                type="number"
                step="0.000001"
                value={formData.currentPrice}
                onChange={(e) => handleInputChange('currentPrice', e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
                placeholder="例如: 0.342"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/90 mb-1">加仓金额</label>
              <input
                type="number"
                step="0.01"
                value={formData.addAmount}
                onChange={(e) => handleInputChange('addAmount', e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
                placeholder="例如: 10000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/90 mb-1">加仓价格</label>
              <input
                type="number"
                step="0.000001"
                value={formData.addPrice}
                onChange={(e) => handleInputChange('addPrice', e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
                placeholder="例如: 0.39"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/90 mb-1">目标成本价</label>
              <input
                type="number"
                step="0.000001"
                value={formData.targetCost}
                onChange={(e) => handleInputChange('targetCost', e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
                placeholder="例如: 0.31"
              />
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <p className="text-red-400 text-sm whitespace-pre-line">{error}</p>
              </div>
            )}

            {result && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                <div className="text-green-400 text-sm font-medium whitespace-pre-line">
                  {result.split('\n').map((line, index) => {
                    if (line.includes('盈亏:')) {
                      const isPositive =
                        line.includes('+') ||
                        (!line.includes('-') && parseFloat(line.match(/-?\d+\.?\d*/)?.[0] || '0') > 0);
                      return (
                        <div key={index} className={`${isPositive ? 'text-green-400' : 'text-red-400'} font-semibold`}>
                          {line}
                        </div>
                      );
                    }
                    return <div key={index}>{line}</div>;
                  })}
                </div>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={calculate}
                className="flex-1 bg-cyan-500/20 text-cyan-300 border border-cyan-500/50 font-semibold py-3 px-4 rounded-lg hover:bg-cyan-500/30 transition-colors"
              >
                计算
              </button>
              <button
                onClick={clearForm}
                className="flex-1 bg-white/10 text-white border border-white/20 font-semibold py-3 px-4 rounded-lg hover:bg-white/20 transition-colors"
              >
                清空
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tools;
